import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  ArrowUpRight, ArrowRight, ArrowLeft, ChevronRight, Shield, TrendingUp, Building2,
  Landmark, Layers, FileCheck, Globe2, Scale, Coins, Boxes, Users2, LineChart,
  Sparkles, CheckCircle2, MapPin, Mail, Phone, Calendar, X, FlaskConical
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------- KPI Counter -------------------- */
const Counter = ({ from = 0, to, prefix = '', suffix = '', decimals = 0, duration = 2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const count = useMotionValue(from);
  const rounded = useTransform(count, (v) => `${prefix}${v.toFixed(decimals)}${suffix}`);
  const [display, setDisplay] = useState(`${prefix}${from.toFixed(decimals)}${suffix}`);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(count, to, { duration, ease: EASE });
    const unsub = rounded.on('change', setDisplay);
    return () => { controls.stop(); unsub(); };
  }, [inView, to, count, rounded, duration]);

  return <span ref={ref} className="font-institutional-data">{display}</span>;
};

/* -------------------- Reveal -------------------- */
const Reveal = ({ children, delay = 0, y = 24, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

/* -------------------- Section overline -------------------- */
const Overline = ({ children, className = '' }) => (
  <p className={`uppercase text-xs tracking-[0.24em] text-[#D4AF37] font-semibold font-institutional-body ${className}`}>
    {children}
  </p>
);

/* -------------------- Header -------------------- */
const Header = ({ onCta }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? 'bg-[#040F2D]/90 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-home">
          <img
            src="https://customer-assets.emergentagent.com/job_563d6243-6438-4f0a-a4a2-1ab5ee42a002/artifacts/gk86wjkd_Logo_Goldcif-removebg-preview.png"
            alt="GOLDCIF"
            className="h-9 w-auto"
          />
        </Link>
        <nav className="hidden lg:flex gap-10 text-sm font-medium text-white/70 font-institutional-body">
          <a href="#opportunity" className="hover:text-white transition-colors">Opportunity</a>
          <a href="#invest" className="hover:text-white transition-colors">Invest</a>
          <a href="#milestones" className="hover:text-white transition-colors">Milestones</a>
          <a href="#team" className="hover:text-white transition-colors">Team</a>
        </nav>
        <button
          onClick={onCta}
          data-testid="header-request-memo-btn"
          className="hidden md:inline-flex items-center gap-2 bg-[#D4AF37] text-[#020817] font-medium text-sm px-6 py-3 hover:bg-[#F3E5AB] transition-colors font-institutional-body"
        >
          Request Memorandum <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
    </header>
  );
};

/* -------------------- Hero -------------------- */
const Hero = ({ onPrimary, onSecondary }) => (
  <section className="relative min-h-screen flex items-end overflow-hidden bg-[#040F2D]">
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1762463176417-2c5e63767bd2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYnVsbGlvbiUyMHZhdWx0JTIwc2VjdXJlJTIwZmluYW5jZXxlbnwwfHx8fDE3ODMxMjIzOTF8MA&ixlib=rb-4.1.0&q=85"
        alt="Institutional gold vault"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#040F2D] via-[#040F2D]/70 to-[#040F2D]/50"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#040F2D]/80 via-transparent to-[#040F2D]/40"></div>
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-24 md:pb-32 pt-32 w-full">
      <Reveal delay={0.2}>
        <Overline>Institutional Investment Platform</Overline>
      </Reveal>
      <Reveal delay={0.35}>
        <h1 className="font-institutional-heading text-white text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-semibold leading-[0.95] tracking-tight max-w-5xl mt-6">
          Invest in Africa&apos;s Cutting Edge Gold Trading Infrastructure
        </h1>
      </Reveal>
      <Reveal delay={0.5}>
        <p className="text-white/70 text-lg md:text-xl max-w-2xl mt-8 font-institutional-body font-light leading-relaxed">
          GoldCIF builds the infrastructure that transforms informal artisanal gold trading into transparent, compliant and investable transactions.
        </p>
      </Reveal>
      <Reveal delay={0.65}>
        <div className="flex flex-wrap gap-4 mt-12">
          <button
            onClick={onPrimary}
            data-testid="hero-request-memo-btn"
            className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#020817] font-medium px-8 py-4 hover:bg-[#F3E5AB] transition-all font-institutional-body"
          >
            Request Investment Memorandum
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </button>
          <button
            onClick={onSecondary}
            data-testid="hero-schedule-call-btn"
            className="group inline-flex items-center gap-3 bg-transparent border border-[#D4AF37] text-[#D4AF37] font-medium px-8 py-4 hover:bg-[#D4AF37]/10 transition-all font-institutional-body"
          >
            Schedule an Investor Call
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </Reveal>
      <Reveal delay={0.8}>
        <div className="mt-16 flex items-center gap-8 text-white/50 text-xs tracking-[0.2em] uppercase font-institutional-body">
          <span>Licensed</span>
          <span className="w-8 h-px bg-white/30"></span>
          <span>OECD Aligned</span>
          <span className="w-8 h-px bg-white/30"></span>
          <span>Uganda &middot; Dubai</span>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------- Key Investment Metrics -------------------- */
const KPIs = () => {
  const items = [
    { label: 'Official Uganda Gold Exports', value: 'US$5.2B', sub: 'Annual formal exports' },
    { label: 'Monthly Capacity per Trading Center', value: '200 KG', sub: 'GTRADE throughput' },
    { label: 'Investment Opportunities', value: '3', sub: 'Distinct product lines' },
    { label: 'Licensed Trade Corridor', value: 'UG → DXB', sub: 'Uganda to Dubai' },
    { label: 'Licensed Operations', value: '100%', sub: 'Fully compliant' },
    { label: 'Business Model', value: 'Multi-Revenue', sub: 'Infrastructure play' },
  ];
  return (
    <section className="bg-[#020817] py-24 md:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <Overline>Key Investment Metrics</Overline>
          <h2 className="font-institutional-heading text-white text-3xl md:text-4xl lg:text-5xl font-semibold mt-4 max-w-3xl leading-tight">
            The scale of the corridor at a glance.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 mt-16">
          {items.map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: EASE }}
              className="bg-[#020817] p-10 md:p-12 group hover:bg-[#0B132B] transition-colors"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="text-[#D4AF37]/70 font-institutional-data text-xs tracking-widest">0{i + 1}</span>
                <ArrowUpRight className="w-4 h-4 text-white/20 group-hover:text-[#D4AF37] transition-colors" />
              </div>
              <div className="font-institutional-data text-white text-4xl md:text-5xl font-medium tracking-tighter">
                {item.value}
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white text-sm font-medium font-institutional-body">{item.label}</p>
                <p className="text-white/40 text-xs mt-1 font-institutional-body">{item.sub}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- The Opportunity -------------------- */
const Opportunity = () => {
  const problems = [
    'Fragmented supply chain',
    'Information asymmetry',
    'Limited transparency',
    'Execution risk',
    'Difficult access to international buyers',
    'Lack of compliant trading infrastructure',
  ];
  const flow = ['Onboarding', 'Formalization', 'Smelting', 'Trading', 'Export'];
  return (
    <section id="opportunity" className="bg-[#040F2D] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>The Opportunity</Overline>
              <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 leading-[1.02] tracking-tight">
                Africa&apos;s Largest Gold Infrastructure Opportunity
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <Reveal delay={0.15}>
              <p className="text-white/70 text-lg md:text-xl font-institutional-body font-light leading-relaxed">
                The challenge is the absence of trusted infrastructure connecting artisanal producers with international standards, legalisation and capital market in the same place.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 mt-10">
                {problems.map((p) => (
                  <div key={p} className="bg-[#040F2D] p-6 flex items-start gap-3">
                    <X className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                    <span className="text-white/80 font-institutional-body text-sm">{p}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.45}>
              <p className="text-white text-lg font-institutional-body font-light leading-relaxed mt-8">
                GoldCIF solves these problems through <span className="text-[#D4AF37]">infrastructure, compliance and execution</span>.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Ecosystem flow */}
        <Reveal delay={0.15}>
          <div className="mt-24 border-t border-white/10 pt-16">
            <Overline className="mb-8">The Flow</Overline>
            <div className="flex flex-wrap items-center gap-y-6 gap-x-3">
              {flow.map((node, i) => (
                <React.Fragment key={node}>
                  <div className="group cursor-default">
                    <div className="border border-white/10 hover:border-[#D4AF37]/60 transition-all px-5 py-3 md:px-6 md:py-4">
                      <span className="text-[#D4AF37]/60 font-institutional-data text-[10px] tracking-widest">
                        0{i + 1}
                      </span>
                      <p className="text-white font-institutional-heading font-medium mt-1 text-sm md:text-base">{node}</p>
                    </div>
                  </div>
                  {i < flow.length - 1 && <ChevronRight className="w-4 h-4 text-[#D4AF37]/40 hidden md:block" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* -------------------- Market Opportunity + Map -------------------- */
const Market = () => (
  <section className="bg-[#020817] py-24 md:py-32 border-y border-white/5">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <Overline>Market Opportunity</Overline>
            <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 tracking-tight leading-[1.02]">
              A licensed corridor between Uganda and Dubai.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 space-y-8">
              <div>
                <p className="text-[#D4AF37] text-xs tracking-[0.24em] uppercase font-institutional-body">Official Uganda Gold Exports</p>
                <p className="font-institutional-data text-white text-6xl md:text-7xl font-medium mt-2 tracking-tighter">
                  <Counter from={0} to={5.2} decimals={1} prefix="US$" suffix="B" duration={2.4} />
                </p>
                <p className="text-white/50 text-sm mt-3 font-institutional-body">Reported annual formal exports.</p>
              </div>
              <div className="border-t border-white/10 pt-8">
                <p className="text-white/80 text-base md:text-lg font-institutional-body font-light leading-relaxed">
                  The formal gold market represents only a share of the regional opportunity. As artisanal producers move into regulated international trade, the addressable market expands significantly — driven by rising demand for traceability, OECD compliance, and responsible sourcing.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Formal vs Informal bar chart */}
        <div className="lg:col-span-6">
          <Reveal delay={0.3}>
            <div className="relative bg-[#040F2D] border border-white/10 p-8 md:p-10">
              <p className="text-[#D4AF37] text-xs tracking-[0.24em] uppercase font-institutional-body">Regional Market Split</p>
              <p className="font-institutional-heading text-white text-2xl md:text-3xl font-semibold mt-3 tracking-tight leading-tight">
                Only 15% is formalized today.
              </p>

              <div className="mt-10 space-y-8">
                {/* Formal bar */}
                <div>
                  <div className="flex items-baseline justify-between mb-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-[0.2em] font-institutional-body">Formal Exports</p>
                      <p className="font-institutional-data text-white text-3xl md:text-4xl mt-1 tracking-tighter">US$5.2B</p>
                    </div>
                    <span className="font-institutional-data text-[#D4AF37] text-sm">15%</span>
                  </div>
                  <div className="w-full h-3 bg-white/5 relative overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '15%' }}
                      viewport={{ once: true, margin: '-40px' }}
                      transition={{ duration: 1.8, ease: EASE }}
                      className="h-full bg-[#D4AF37]"
                    />
                  </div>
                </div>

                {/* Informal bar */}
                <div>
                  <div className="flex items-baseline justify-between mb-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-[0.2em] font-institutional-body">Informal Exports · Target</p>
                      <p className="font-institutional-data text-white text-3xl md:text-4xl mt-1 tracking-tighter">US$30B</p>
                    </div>
                    <span className="font-institutional-data text-[#D4AF37] text-sm">85%</span>
                  </div>
                  <div className="w-full h-3 bg-white/5 relative overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '85%' }}
                      viewport={{ once: true, margin: '-40px' }}
                      transition={{ duration: 2.2, ease: EASE, delay: 0.3 }}
                      className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] relative"
                    >
                      <div className="absolute inset-0 flex items-center justify-end pr-4">
                        <span className="text-[#020817] text-[10px] uppercase tracking-[0.2em] font-institutional-body font-bold whitespace-nowrap">This is our target</span>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-white/10">
                <p className="text-white/50 text-xs font-institutional-body leading-relaxed">
                  The addressable market grows as artisanal producers move from informal channels into the regulated corridor.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* Abstract dot map of Africa + UAE */
const AfricaMap = () => {
  // Africa continent silhouette (simplified path) + dots for Uganda and Dubai
  return (
    <svg viewBox="0 0 500 500" className="w-full h-full">
      <defs>
        <linearGradient id="glow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      {/* Simplified Africa dot cloud */}
      {[
        [240,110],[255,120],[270,115],[285,125],[220,130],[235,140],[250,145],[268,150],[290,145],[310,155],
        [200,155],[220,168],[240,175],[262,180],[285,185],[308,190],[195,190],[220,200],[245,210],[270,215],
        [295,220],[315,215],[180,225],[210,235],[240,245],[265,250],[290,255],[310,250],[195,265],[225,275],
        [255,285],[285,290],[305,285],[210,305],[240,315],[275,320],[298,315],[225,340],[260,350],[290,345],
        [245,370],[275,375],[260,395],[280,395],[245,410],
      ].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill="#D4AF37" opacity="0.35" />
      ))}
      {/* Uganda location */}
      <g>
        <circle cx="278" cy="255" r="9" fill="#D4AF37" opacity="0.2">
          <animate attributeName="r" values="9;18;9" dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0;0.3" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="278" cy="255" r="5" fill="#D4AF37" />
        <text x="290" y="260" fill="#fff" fontSize="12" fontFamily="Inter Tight, sans-serif" fontWeight="500">Uganda</text>
      </g>
      {/* Dubai location */}
      <g>
        <circle cx="395" cy="205" r="9" fill="#D4AF37" opacity="0.2">
          <animate attributeName="r" values="9;18;9" dur="3s" begin="0.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0;0.3" dur="3s" begin="0.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="395" cy="205" r="5" fill="#D4AF37" />
        <text x="405" y="210" fill="#fff" fontSize="12" fontFamily="Inter Tight, sans-serif" fontWeight="500">Dubai</text>
      </g>
      {/* Corridor line */}
      <line x1="278" y1="255" x2="395" y2="205" stroke="url(#glow)" strokeWidth="1.5" strokeDasharray="4 4">
        <animate attributeName="stroke-dashoffset" values="0;-16" dur="1.5s" repeatCount="indefinite" />
      </line>
    </svg>
  );
};

/* -------------------- Why Now -------------------- */
const WhyNow = () => {
  const items = [
    { icon: Shield, title: 'Responsible Gold', text: 'Rising demand for ethical sourcing across major buyers.' },
    { icon: FileCheck, title: 'OECD Due Diligence', text: 'International buyers require verified chain-of-custody.' },
    { icon: Scale, title: 'AML Requirements', text: 'Stricter anti-money-laundering across global commodity flows.' },
    { icon: Layers, title: 'Traceability', text: 'End-to-end tracking is now expected, not optional.' },
    { icon: Landmark, title: 'Government Formalization', text: 'Regulators actively supporting formal artisanal trade.' },
    { icon: Globe2, title: 'International Demand', text: 'Growing appetite for compliant, non-conflict gold.' },
    { icon: Coins, title: 'Digital Payments', text: 'Faster, safer settlement infrastructure across corridors.' },
    { icon: TrendingUp, title: 'Institutional Capital', text: 'Private equity entering African commodities at scale.' },
  ];
  return (
    <section className="bg-[#040F2D] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <Overline>Why Now</Overline>
          <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 max-w-3xl leading-[1.02] tracking-tight">
            Eight forces are converging on this market.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 mt-16">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.8, delay: i * 0.06, ease: EASE }}
                className="bg-[#040F2D] p-8 md:p-10 group hover:bg-[#0B132B] transition-colors flex flex-col justify-between min-h-[240px]"
              >
                <Icon className="w-7 h-7 text-[#D4AF37] stroke-[1.5]" />
                <div className="mt-8">
                  <h3 className="font-institutional-heading text-white font-medium text-lg tracking-tight">{it.title}</h3>
                  <p className="text-white/50 text-sm mt-3 font-institutional-body leading-relaxed">{it.text}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* -------------------- Equity Pie Chart -------------------- */
const EquityPie = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#040F2D] to-[#020817] p-8">
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px] h-auto">
      <defs>
        <filter id="pieShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#D4AF37" floodOpacity="0.35" />
        </filter>
      </defs>
      {/* Main pie (rest) */}
      <motion.path
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 1.4, ease: EASE }}
        d="M 100 100 L 100 20 A 80 80 0 1 1 43.4 143.4 Z"
        fill="#D4AF37"
        opacity="0.9"
      />
      {/* Detached slice */}
      <motion.g
        initial={{ x: 0, y: 0, opacity: 0 }}
        whileInView={{ x: 18, y: -18, opacity: 1 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
        filter="url(#pieShadow)"
      >
        <path d="M 100 100 L 43.4 143.4 A 80 80 0 0 1 100 20 Z" fill="#F3E5AB" />
      </motion.g>
      {/* Center hole for donut effect */}
      <circle cx="100" cy="100" r="34" fill="#020817" />
      <text x="100" y="97" textAnchor="middle" fill="#D4AF37" fontSize="8" fontFamily="Inter Tight" letterSpacing="1.5" fontWeight="600">EQUITY</text>
      <text x="100" y="109" textAnchor="middle" fill="#fff" fontSize="9" fontFamily="Inter Tight" fontWeight="500">Your Share</text>
    </svg>
  </div>
);

/* -------------------- Three Ways to Invest (SIGNATURE) -------------------- */
const ThreeWays = ({ onCta }) => {
  const products = [
    {
      num: '01',
      code: 'BlockGold Capital',
      title: 'Gold Margin Investments',
      desc: 'Invest alongside verified physical gold transactions. Capital is deployed only after gold has been tested, verified and approved through our operating process. Follow everything from your phone.',
      metrics: [
        { k: 'Investment Size', v: 'US$100K+' },
        { k: 'Target Return', v: '100–125%' },
        { k: 'Revenue Source', v: 'Trading Margin' },
      ],
      flow: ['Product Verification', 'Smelting', 'Sealing', 'Purchase', 'Resale', 'Revenue Distribution'],
      image: 'https://customer-assets.emergentagent.com/job_563d6243-6438-4f0a-a4a2-1ab5ee42a002/artifacts/c0wj23f7_file_000000008ea471f4b4e9abe24b522816.png',
      icon: Coins,
    },
    {
      num: '02',
      code: 'GTRADE CENTER',
      title: 'Infrastructure Investments',
      desc: 'Invest in licensed gold trading and laboratory infrastructure that formalizes artisanal gold trading and generates multiple recurring operational revenues.',
      metrics: [
        { k: 'Approximate Cost', v: 'US$300K per Center' },
        { k: 'Monthly Capacity', v: '200 KG' },
      ],
      revenues: ['Gold Testing', 'Smelting', 'Legalization Services', 'Export Services', 'Transaction Facilities', 'Storage', 'Compliance Services'],
      image: 'https://customer-assets.emergentagent.com/job_563d6243-6438-4f0a-a4a2-1ab5ee42a002/artifacts/5ll8s9nj_file_0000000071a07243a756468a6391cd8a.png',
      icon: Building2,
    },
    {
      num: '03',
      code: 'GoldCIF Equity',
      title: 'Equity Investment',
      desc: 'Become a shareholder in GoldCIF and participate in the long-term growth of the company building Africa\'s trusted gold trading infrastructure.',
      metrics: [
        { k: 'Structure', v: 'Direct Equity' },
        { k: 'Horizon', v: 'Long-Term' },
      ],
      isEquity: true,
      icon: Landmark,
    },
  ];
  return (
    <section id="invest" className="bg-[#020817] py-24 md:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-20">
          <div className="lg:col-span-8">
            <Reveal>
              <Overline>Three Ways to Invest</Overline>
              <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 leading-[1.02] tracking-tight">
                Three distinct products. One infrastructure play.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:pl-8">
            <Reveal delay={0.2}>
              <p className="text-white/60 text-base md:text-lg font-institutional-body font-light leading-relaxed">
                Each opportunity is structured for a specific investor profile — from margin-linked deployment to long-horizon equity.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Three independent entities notice */}
        <Reveal delay={0.3}>
          <div className="mb-16 border border-[#D4AF37]/25 bg-[#040F2D] p-8 md:p-10 grid md:grid-cols-4 gap-8 md:gap-4 items-center">
            <div className="md:col-span-1">
              <Overline>Governance Structure</Overline>
              <p className="font-institutional-heading text-white text-lg md:text-xl font-semibold mt-3 tracking-tight leading-tight">
                Three independent vehicles.
              </p>
            </div>
            <div className="md:col-span-3 md:pl-8 md:border-l md:border-white/10">
              <p className="text-white/70 text-sm md:text-base font-institutional-body font-light leading-relaxed">
                <span className="text-[#D4AF37]">BlockGold Capital</span>, <span className="text-[#D4AF37]">GTRADE CENTER</span> and <span className="text-[#D4AF37]">GoldCIF</span> are three separate legal entities, each with its own shareholders, capital structure and operational independence. All three are piloted by GoldCIF as the group operator — enabling investors to choose the vehicle that best fits their mandate without cross-exposure between products.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="space-y-px bg-white/5">
          {products.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 1, delay: i * 0.08, ease: EASE }}
                className="bg-[#040F2D] group"
              >
                <div className="grid lg:grid-cols-12 gap-0">
                  <div className={`lg:col-span-5 relative overflow-hidden h-72 lg:h-auto lg:min-h-[420px] bg-[#020817] ${p.isEquity ? 'lg:min-h-[520px]' : ''}`}>
                    {p.isEquity ? (
                      <EquityPie />
                    ) : (
                      <>
                        <img src={p.image} alt={p.code} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out" />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#040F2D] via-[#040F2D]/40 to-transparent"></div>
                      </>
                    )}
                    <div className="absolute top-8 left-8">
                      <span className="font-institutional-data text-[#D4AF37] text-sm tracking-widest">{p.num}</span>
                    </div>
                    <div className="absolute bottom-8 left-8">
                      <Icon className="w-8 h-8 text-[#D4AF37] stroke-[1.5]" />
                    </div>
                  </div>
                  <div className="lg:col-span-7 p-10 md:p-14 lg:p-16 flex flex-col justify-between">
                    <div>
                      <p className="font-institutional-body text-[#D4AF37] text-xs tracking-[0.24em] uppercase">{p.code}</p>
                      <h3 className="font-institutional-heading text-white text-3xl md:text-4xl lg:text-5xl font-semibold mt-3 tracking-tight leading-tight">
                        {p.title}
                      </h3>
                      <p className="text-white/60 mt-6 text-base md:text-lg font-institutional-body font-light leading-relaxed max-w-2xl">
                        {p.desc}
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-10 border-t border-white/10 pt-8">
                        {p.metrics.map((m) => (
                          <div key={m.k}>
                            <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-institutional-body">{m.k}</p>
                            <p className="font-institutional-data text-white text-2xl md:text-3xl mt-2 tracking-tighter">{m.v}</p>
                          </div>
                        ))}
                      </div>

                      {p.flow && (
                        <div className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-3">
                          {p.flow.map((step, idx) => (
                            <React.Fragment key={step}>
                              <span className="text-white/70 text-xs md:text-sm border border-white/10 px-3 py-1.5 font-institutional-body">{step}</span>
                              {idx < p.flow.length - 1 && <ChevronRight className="w-3 h-3 text-[#D4AF37]/50" />}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      {p.revenues && (
                        <div className="mt-8">
                          <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-institutional-body mb-4">Revenue Sources</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
                            {p.revenues.map((r) => (
                              <div key={r} className="flex items-center gap-2">
                                <div className="w-1 h-1 bg-[#D4AF37]"></div>
                                <span className="text-white/80 text-sm font-institutional-body">{r}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-10 pt-8 border-t border-white/10">
                      <button
                        onClick={() => onCta(p.code)}
                        data-testid={`invest-product-${i}-btn`}
                        className="group/btn inline-flex items-center gap-3 text-[#D4AF37] text-sm font-medium tracking-wide font-institutional-body hover:text-white transition-colors"
                      >
                        Request details on {p.code}
                        <ArrowUpRight className="w-4 h-4 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* -------------------- Ecosystem -------------------- */
const Ecosystem = () => {
  const nodes = ['Artisanal Sellers', 'GoldCIF', 'GTRADE', 'Testing', 'Legalization', 'Export', 'Dubai', 'International Buyers', 'Capital Returns'];
  return (
    <section id="ecosystem" className="bg-[#040F2D] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <Overline>Ecosystem</Overline>
          <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 max-w-3xl leading-[1.02] tracking-tight">
            Every layer reinforces the next.
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
          {nodes.map((n, i) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: EASE }}
              className="bg-[#040F2D] px-8 py-10 group hover:bg-[#0B132B] transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-5">
                <span className="font-institutional-data text-[#D4AF37]/70 text-xs tracking-widest w-8">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="font-institutional-heading text-white text-lg md:text-xl font-medium tracking-tight">{n}</span>
              </div>
              <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- Revenue Engine -------------------- */
const Revenue = () => {
  const revs = ['Trading Margin', 'Gold Testing', 'Smelting', 'Legalization', 'Export Services', 'Transaction Rooms', 'Storage', 'Compliance', 'Membership Services'];
  return (
    <section className="bg-[#020817] py-24 md:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Revenue Engine</Overline>
              <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 leading-[1.02] tracking-tight">
                Eight recurring revenue streams.
              </h2>
              <p className="text-white/60 mt-8 text-base md:text-lg font-institutional-body font-light leading-relaxed">
                A diversified operating model built around a physical infrastructure asset. Each trading center generates income across trading, services and compliance.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
              {revs.map((r, i) => (
                <motion.div
                  key={r}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: EASE }}
                  className="bg-[#020817] p-8 min-h-[140px] flex flex-col justify-between hover:bg-[#0B132B] transition-colors"
                >
                  <span className="font-institutional-data text-[#D4AF37]/60 text-[10px] tracking-widest">R.{String(i + 1).padStart(2, '0')}</span>
                  <p className="text-white text-base font-institutional-heading font-medium tracking-tight">{r}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- Why GoldCIF -------------------- */
const WhyGoldCIF = () => {
  const items = [
    'Exclusive Artisanal Gold Producers Networks',
    'Local Respected Offtaker',
    'Physical and Digital Infrastructure',
    'Ground Experienced',
    'Local International Execution Team',
    'Multiple Revenue Streams',
    'Scalable Infrastructure Model',
    'Operational Transparency',
    'Fully Licensed',
  ];
  return (
    <section className="bg-[#040F2D] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <Overline>Why GoldCIF</Overline>
          <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 max-w-3xl leading-[1.02] tracking-tight">
            Built for scale, licensed for trust.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 mt-16">
          {items.map((it, i) => (
            <motion.div
              key={it}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: EASE }}
              className="bg-[#040F2D] p-6 md:p-8 min-h-[180px] flex flex-col justify-between hover:bg-[#0B132B] transition-colors group"
            >
              <CheckCircle2 className="w-5 h-5 text-[#D4AF37] stroke-[1.5]" />
              <p className="text-white font-institutional-heading font-medium text-sm md:text-base leading-tight tracking-tight mt-6">{it}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- Milestones -------------------- */
const Milestones = () => {
  const items = [
    { label: 'Company Formation', status: 'Completed' },
    { label: 'Licensing', status: 'Completed' },
    { label: 'Operational Launch', status: 'Completed' },
    { label: 'Seller Network', status: 'In Progress' },
    { label: 'Uganda Operations', status: 'In Progress' },
    { label: 'Dubai Corridor', status: 'In Progress' },
    { label: 'Technology Platform', status: 'Roadmap' },
    { label: 'Regional Expansion', status: 'Roadmap' },
  ];
  return (
    <section id="milestones" className="bg-[#020817] py-24 md:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <Overline>Milestones</Overline>
          <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 max-w-3xl leading-[1.02] tracking-tight">
            Execution roadmap.
          </h2>
        </Reveal>
        <div className="mt-16 relative">
          <div className="absolute left-3 md:left-4 top-0 bottom-0 w-px bg-white/10"></div>
          {items.map((it, i) => (
            <motion.div
              key={it.label}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
              className="relative pl-12 md:pl-16 py-6 border-b border-white/5 last:border-0 flex items-center justify-between group"
            >
              <div className={`absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-3 h-3 border-2 ${it.status === 'Completed' ? 'bg-[#D4AF37] border-[#D4AF37]' : it.status === 'In Progress' ? 'bg-transparent border-[#D4AF37]' : 'bg-transparent border-white/30'}`}></div>
              <div className="flex items-center gap-6">
                <span className="font-institutional-data text-white/40 text-xs">M.{String(i + 1).padStart(2, '0')}</span>
                <p className="font-institutional-heading text-white text-lg md:text-xl font-medium tracking-tight">{it.label}</p>
              </div>
              <span className={`font-institutional-body text-xs uppercase tracking-[0.2em] ${it.status === 'Completed' ? 'text-[#D4AF37]' : it.status === 'In Progress' ? 'text-white/80' : 'text-white/40'}`}>
                {it.status}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- Management Team (text-based) -------------------- */
const Team = () => {
  const areas = [
    { title: 'Mining', text: 'Field operations, artisanal producer network, on-site licensing execution.' },
    { title: 'Banking', text: 'Structured commodity finance, treasury operations, capital controls.' },
    { title: 'Private Equity', text: 'Deal structuring, investor relations, portfolio governance.' },
    { title: 'International Trade', text: 'Export logistics, Dubai corridor operations, offtaker relationships.' },
    { title: 'Finance', text: 'Financial modeling, transaction reconciliation, reporting standards.' },
    { title: 'Operations', text: 'Trading center management, compliance, integrated technology.' },
  ];
  return (
    <section id="team" className="bg-[#040F2D] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-20">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>Management Team</Overline>
              <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-6xl font-semibold mt-4 leading-[1.02] tracking-tight">
                Institutional expertise across the value chain.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <Reveal delay={0.2}>
              <p className="text-white/60 text-base md:text-lg font-institutional-body font-light leading-relaxed">
                An executive team combining decades of experience in mining, banking, private equity, international trade, finance and operations.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="divide-y divide-white/10 border-y border-white/10">
          {areas.map((a, i) => (
            <motion.div
              key={a.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: EASE }}
              className="py-10 grid grid-cols-12 gap-6 items-center group hover:bg-[#0B132B] transition-colors px-2"
            >
              <div className="col-span-2 md:col-span-1 font-institutional-data text-[#D4AF37]/60 text-xs">
                E.{String(i + 1).padStart(2, '0')}
              </div>
              <div className="col-span-10 md:col-span-4 font-institutional-heading text-white text-xl md:text-2xl font-semibold tracking-tight">
                {a.title}
              </div>
              <div className="col-span-12 md:col-span-7 text-white/60 text-sm md:text-base font-institutional-body font-light leading-relaxed">
                {a.text}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- Final CTA -------------------- */
const FinalCTA = ({ onPrimary, onSecondary }) => (
  <section className="relative bg-[#020817] py-24 md:py-40 overflow-hidden">
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1634007626524-f47fa37810a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMHNreWxpbmUlMjBuaWdodCUyMGx1eHVyeXxlbnwwfHx8fDE3ODMxMjI0MTF8MA&ixlib=rb-4.1.0&q=85"
        alt="Dubai skyline"
        className="w-full h-full object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#020817] via-[#020817]/80 to-[#020817]/60"></div>
    </div>
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
      <Reveal>
        <Overline>Confidential Investor Access</Overline>
      </Reveal>
      <Reveal delay={0.15}>
        <h2 className="font-institutional-heading text-white text-4xl md:text-5xl lg:text-7xl font-semibold mt-6 leading-[1.02] tracking-tight max-w-5xl">
          Invest in the Infrastructure Powering Africa&apos;s Formal Gold Economy.
        </h2>
      </Reveal>
      <Reveal delay={0.3}>
        <p className="text-white/60 text-base md:text-xl font-institutional-body font-light leading-relaxed mt-8 max-w-3xl">
          Request our confidential Investment Memorandum and speak directly with our executive team about current investment opportunities.
        </p>
      </Reveal>
      <Reveal delay={0.45}>
        <div className="flex flex-wrap gap-4 mt-12">
          <button
            onClick={onPrimary}
            data-testid="final-request-memo-btn"
            className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#020817] font-medium px-8 py-4 hover:bg-[#F3E5AB] transition-colors font-institutional-body"
          >
            Request Investment Memorandum
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </button>
          <button
            onClick={onSecondary}
            data-testid="final-schedule-call-btn"
            className="group inline-flex items-center gap-3 bg-transparent border border-[#D4AF37] text-[#D4AF37] font-medium px-8 py-4 hover:bg-[#D4AF37]/10 transition-colors font-institutional-body"
          >
            Schedule Investor Call
            <Calendar className="w-5 h-5" />
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------- Footer -------------------- */
const InvestorFooter = () => (
  <footer className="bg-[#040F2D] border-t border-white/10 py-16">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
      <div className="grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img
            src="https://customer-assets.emergentagent.com/job_563d6243-6438-4f0a-a4a2-1ab5ee42a002/artifacts/gk86wjkd_Logo_Goldcif-removebg-preview.png"
            alt="GOLDCIF"
            className="h-10 w-auto"
          />
          <p className="text-white/50 text-sm mt-6 max-w-md font-institutional-body font-light leading-relaxed">
            Building the legal, financial and physical infrastructure that formalizes Africa&apos;s artisanal gold trade.
          </p>
        </div>
        <div>
          <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-institutional-body">Contact</p>
          <a href="mailto:compliance@goldcif.com" className="block text-white/70 text-sm mt-4 hover:text-white transition-colors font-institutional-body">
            compliance@goldcif.com
          </a>
        </div>
        <div>
          <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-institutional-body">Operations</p>
          <p className="text-white/70 text-sm mt-4 font-institutional-body">Uganda &middot; Dubai</p>
        </div>
      </div>
      <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between gap-4">
        <p className="text-white/40 text-xs font-institutional-body">© {new Date().getFullYear()} GOLDCIF. All rights reserved.</p>
        <p className="text-white/40 text-xs font-institutional-body">Confidential Investment Platform. Not a public offering.</p>
      </div>
    </div>
  </footer>
);

/* -------------------- Modal (Memorandum / Call) -------------------- */
const InvestorModal = ({ open, onClose, mode, presetService }) => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', country: '',
    serviceInterest: presetService || (mode === 'call' ? 'All Services' : 'BlockGold Capital'),
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData((f) => ({ ...f, serviceInterest: presetService || (mode === 'call' ? 'All Services' : 'BlockGold Capital') }));
    setSuccess(false);
    setError('');
  }, [open, mode, presetService]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/investor-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString(),
          type: mode === 'call' ? 'investor_call' : 'investor_memorandum',
        }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        setError('We could not submit your request. Please try again in a moment.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="investor-modal">
      <div className="bg-[#020817] border border-white/10 max-w-2xl w-full p-8 md:p-12 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="investor-modal-close" className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>
        {success ? (
          <div className="text-center py-12">
            <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto stroke-[1.5]" />
            <h3 className="font-institutional-heading text-white text-3xl font-semibold mt-8 tracking-tight">Request received.</h3>
            <p className="text-white/60 mt-4 font-institutional-body font-light leading-relaxed">
              A member of our executive team will contact you within 24 hours to discuss{' '}
              {mode === 'call' ? 'scheduling your investor call' : 'sharing our Investment Memorandum'}.
            </p>
            <button onClick={onClose} className="mt-8 inline-flex items-center gap-2 text-[#D4AF37] font-medium font-institutional-body hover:text-white transition-colors">
              Close <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <Overline>{mode === 'call' ? 'Schedule an Investor Call' : 'Request Investment Memorandum'}</Overline>
            <h3 className="font-institutional-heading text-white text-3xl md:text-4xl font-semibold mt-4 tracking-tight leading-tight">
              {mode === 'call' ? 'Speak with our executive team.' : 'Receive our confidential document.'}
            </h3>
            <p className="text-white/50 mt-4 font-institutional-body font-light text-sm">
              Confidential intake. Our team will contact you within 24 hours.
            </p>
            <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-[0.2em] font-institutional-body">First Name</Label>
                <Input required data-testid="modal-firstname" value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="bg-transparent border-white/20 text-white mt-2 rounded-none focus:border-[#D4AF37]" />
              </div>
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-[0.2em] font-institutional-body">Last Name</Label>
                <Input required data-testid="modal-lastname" value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="bg-transparent border-white/20 text-white mt-2 rounded-none focus:border-[#D4AF37]" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-white/60 text-xs uppercase tracking-[0.2em] font-institutional-body">Email</Label>
                <Input required type="email" data-testid="modal-email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-transparent border-white/20 text-white mt-2 rounded-none focus:border-[#D4AF37]" />
              </div>
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-[0.2em] font-institutional-body">Country</Label>
                <Input required data-testid="modal-country" value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="bg-transparent border-white/20 text-white mt-2 rounded-none focus:border-[#D4AF37]" />
              </div>
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-[0.2em] font-institutional-body">Interest</Label>
                <select data-testid="modal-interest" value={formData.serviceInterest}
                  onChange={(e) => setFormData({ ...formData, serviceInterest: e.target.value })}
                  className="w-full bg-transparent border border-white/20 text-white mt-2 py-2 px-3 focus:border-[#D4AF37] focus:outline-none font-institutional-body">
                  <option value="BlockGold Capital" className="bg-[#020817]">BlockGold Capital</option>
                  <option value="GTRADE CENTER" className="bg-[#020817]">GTRADE CENTER</option>
                  <option value="GoldCIF Equity" className="bg-[#020817]">GoldCIF Equity</option>
                  <option value="All Services" className="bg-[#020817]">All Services</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-white/60 text-xs uppercase tracking-[0.2em] font-institutional-body">Message (Optional)</Label>
                <Textarea data-testid="modal-message" rows={3} value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-transparent border-white/20 text-white mt-2 rounded-none focus:border-[#D4AF37]" />
              </div>
              <div className="md:col-span-2 mt-4">
                {error && (
                  <p className="text-red-400 text-sm mb-4 font-institutional-body" data-testid="modal-error">{error}</p>
                )}
                <Button type="submit" disabled={submitting} data-testid="modal-submit"
                  className="w-full bg-[#D4AF37] text-[#020817] hover:bg-[#F3E5AB] rounded-none font-medium py-6 font-institutional-body">
                  {submitting ? 'Submitting...' : (mode === 'call' ? 'Schedule the Call' : 'Request Memorandum')}
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

/* ==================== MAIN PAGE ==================== */
const InvestorLanding = () => {
  const [modal, setModal] = useState({ open: false, mode: 'memo', service: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openMemo = (service = '') => setModal({ open: true, mode: 'memo', service });
  const openCall = () => setModal({ open: true, mode: 'call', service: '' });

  return (
    <div className="bg-[#040F2D] min-h-screen font-institutional-body antialiased">
      <Header onCta={() => openMemo()} />

      <Link to="/" data-testid="back-to-home" className="fixed top-24 left-6 md:left-10 lg:left-16 z-40 flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-[0.2em] transition-colors font-institutional-body">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <Hero onPrimary={() => openMemo()} onSecondary={openCall} />
      <KPIs />
      <Opportunity />
      <Market />
      <WhyNow />
      <ThreeWays onCta={openMemo} />
      <Revenue />
      <WhyGoldCIF />
      <Team />
      <FinalCTA onPrimary={() => openMemo()} onSecondary={openCall} />
      <InvestorFooter />

      <InvestorModal
        open={modal.open}
        mode={modal.mode}
        presetService={modal.service}
        onClose={() => setModal({ ...modal, open: false })}
      />
    </div>
  );
};

export default InvestorLanding;
